import { Application } from 'pixi.js';
import { SFX, type SfxAssetMap } from './audio/SoundManager';
import {
  spawnPlayerBullet,
  updateBullets,
  type Bullet,
} from './combat/bullet';
import {
  createFiringState,
  tickEnemyFiring,
  type EnemyForFiring,
  type FiringConfig,
} from './combat/firing-controller';
import { Loop } from './game/Loop';
import { createHUD } from './hud/hud';
import { createKeyboardInput, type KeyboardInput } from './input/keyboard';
import { spawnEnemies } from './level/enemy-spawner';
import { createLevelState, tickLevel } from './level/level-controller';
import {
  resolveCollisions,
  type EnemyHit,
} from './physics/combat-resolver';
import type { Box } from './physics/collisions';
import { createBulletRenderer } from './scene/bullet-renderer';
import {
  createMainScene,
  loadSceneTextures,
  type SceneTextures,
} from './scene/main-scene';
import { createGameOverScene } from './scene/menu/game-over-scene';
import { createTitleScene } from './scene/menu/title-scene';
import { createSceneManager } from './scene/scene-manager';
import { updatePlayer } from './scene/player-controller';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const PLAYER_SPEED = 400;
const INITIAL_LIVES = 3;

const SFX_ASSETS: SfxAssetMap = {
  shoot: './audio/shoot.wav',
  enemy_hit: './audio/enemy_hit.wav',
  player_hit: './audio/player_hit.wav',
  level_up: './audio/level_up.wav',
};

interface PlayingWorld {
  tick(deltaMS: number): void;
  destroy(): void;
  getState(): { score: number; lives: number; gameOver: boolean };
}

function createPlayingWorld(
  app: Application,
  textures: SceneTextures,
  input: KeyboardInput,
): PlayingWorld {
  const scene = createMainScene(app.stage, textures, {
    canvasWidth: CANVAS_WIDTH,
    canvasHeight: CANVAS_HEIGHT,
  });

  const playerWidth = scene.player.width;
  const moveOpts = {
    speed: PLAYER_SPEED,
    minX: 0,
    maxX: CANVAS_WIDTH - playerWidth,
  };

  const hud = createHUD(app.stage, { canvasWidth: CANVAS_WIDTH });

  const bulletRenderer = createBulletRenderer(scene.container);
  const bullets: { current: Bullet[] } = { current: [] };

  type EnemyEntity = {
    id: number;
    sprite: typeof scene.enemies[number];
    row: number;
    col: number;
  };
  let enemyEntities: EnemyEntity[] = scene.enemies.map((sprite, i) => {
    const meta = scene.layout.enemies[i]!;
    return { id: i, sprite, row: meta.row, col: meta.col };
  });
  let nextEnemyId = enemyEntities.length;

  const gameState = { score: 0, lives: INITIAL_LIVES, gameOver: false };
  let levelState = createLevelState();

  const firingConfig: FiringConfig = {
    level: 1,
    minIntervalMs: 800,
    maxIntervalMs: 2400,
    rng: Math.random,
  };
  let firingState = createFiringState(firingConfig);

  let prevShoot = input.state.shoot;
  let playerBulletAlive = false;

  const loop = new Loop();

  loop.add((dt) => updatePlayer(scene.player, input.state, dt, moveOpts));

  loop.add(() => {
    const justPressed = input.state.shoot && !prevShoot;
    prevShoot = input.state.shoot;
    if (justPressed && !playerBulletAlive) {
      bullets.current.push(
        spawnPlayerBullet({
          x: scene.player.x,
          y: scene.player.y,
          width: scene.player.width,
        }),
      );
      playerBulletAlive = true;
      SFX.play('shoot');
    }
  });

  loop.add((dt) => {
    const enemiesForFiring: EnemyForFiring[] = enemyEntities.map((e) => ({
      x: e.sprite.x,
      y: e.sprite.y,
      width: e.sprite.width,
      height: e.sprite.height,
      row: e.row,
      col: e.col,
    }));
    const result = tickEnemyFiring(firingState, enemiesForFiring, dt, firingConfig);
    firingState = result.state;
    for (const b of result.bulletsToSpawn) bullets.current.push(b);
  });

  loop.add((dt) => {
    bullets.current = updateBullets(bullets.current, dt, CANVAS_HEIGHT);

    const enemyHits: EnemyHit[] = enemyEntities.map((e) => ({
      id: e.id,
      row: e.row,
      x: e.sprite.x,
      y: e.sprite.y,
      w: e.sprite.width,
      h: e.sprite.height,
    }));
    const playerBox: Box = {
      x: scene.player.x,
      y: scene.player.y,
      w: scene.player.width,
      h: scene.player.height,
    };

    const collision = resolveCollisions({
      bullets: bullets.current,
      enemies: enemyHits,
      player: playerBox,
    });
    bullets.current = collision.bullets;

    if (collision.enemyHitCount > 0) {
      SFX.play('enemy_hit');
      const killed = new Set(collision.killedEnemyIds);
      for (let i = enemyEntities.length - 1; i >= 0; i--) {
        const e = enemyEntities[i]!;
        if (!killed.has(e.id)) continue;
        e.sprite.destroy();
        enemyEntities.splice(i, 1);
      }
      gameState.score += collision.scoreDelta;
    }

    if (collision.playerHitCount > 0) {
      SFX.play('player_hit');
      gameState.lives += collision.livesDelta;
      if (gameState.lives <= 0) gameState.gameOver = true;
    }

    playerBulletAlive = bullets.current.some((b) => b.owner === 'player');
    bulletRenderer.sync(bullets.current);

    const levelTick = tickLevel(levelState, enemyEntities.length);
    if (levelTick.leveledUp) {
      levelState = levelTick.state;
      const spawned = spawnEnemies(
        scene.container,
        textures,
        scene.layout,
        nextEnemyId,
      );
      nextEnemyId += spawned.length;
      enemyEntities = spawned.map((s) => ({
        id: s.id,
        sprite: s.sprite,
        row: s.row,
        col: s.col,
      }));
      firingConfig.level = levelState.level;
      firingState = createFiringState(firingConfig);
      SFX.play('level_up');
    }
  });

  return {
    tick(deltaMS: number) {
      loop.tick(deltaMS);
      hud.update({ score: gameState.score, lives: gameState.lives });
    },
    destroy() {
      hud.destroy();
      scene.container.destroy({ children: true });
    },
    getState() {
      return { ...gameState };
    },
  };
}

async function bootstrap(): Promise<void> {
  const mount = document.getElementById('app');
  if (!mount) throw new Error('Mount node #app not found');

  const app = new Application();
  await app.init({
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
    background: '#000000',
    antialias: false,
  });

  mount.appendChild(app.canvas);

  const textures = await loadSceneTextures('./sprites');
  SFX.register(SFX_ASSETS);

  const input = createKeyboardInput();
  input.install();

  const menuConfig = { canvasWidth: CANVAS_WIDTH, canvasHeight: CANVAS_HEIGHT };
  const sm = createSceneManager('menu');

  let world: PlayingWorld | null = null;
  let lastFinalScore = 0;
  let titleScene: ReturnType<typeof createTitleScene> | null = null;
  let gameOverScene: ReturnType<typeof createGameOverScene> | null = null;

  sm.on('menu', () => {
    titleScene = createTitleScene(app.stage, menuConfig);
  });
  sm.onExit('menu', () => {
    titleScene?.destroy();
    titleScene = null;
  });

  sm.on('playing', () => {
    world = createPlayingWorld(app, textures, input);
  });
  sm.onExit('playing', () => {
    if (world) {
      lastFinalScore = world.getState().score;
      world.destroy();
      world = null;
    }
  });

  sm.on('gameOver', () => {
    gameOverScene = createGameOverScene(app.stage, lastFinalScore, menuConfig);
  });
  sm.onExit('gameOver', () => {
    gameOverScene?.destroy();
    gameOverScene = null;
  });

  titleScene = createTitleScene(app.stage, menuConfig);

  let prevSpace = false;
  app.ticker.add((ticker) => {
    const justPressed = input.state.shoot && !prevSpace;
    prevSpace = input.state.shoot;

    if (sm.state === 'menu') {
      if (justPressed) sm.transition('playing');
      return;
    }
    if (sm.state === 'playing') {
      world?.tick(ticker.deltaMS);
      if (world?.getState().gameOver) sm.transition('gameOver');
      return;
    }
    if (sm.state === 'gameOver') {
      if (justPressed) sm.transition('playing');
      return;
    }
  });
}

void bootstrap();
